/**
 * ConfirmDialog — Full-screen blur overlay confirmation dialog with smooth animation
 */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Slide,
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function ConfirmDialog({
  open,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmColor = 'error',
  onConfirm,
  onClose,
  loading = false,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted={false}
      maxWidth="xs"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(6px)',
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
          },
        },
      }}
      PaperProps={{
        elevation: 24,
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          background: 'rgba(255,255,255,0.97)',
          boxShadow: '0 25px 60px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.15)',
          backdropFilter: 'blur(20px)',
        },
      }}
    >
      {/* Colored top stripe */}
      <Box
        sx={{
          height: 4,
          background: confirmColor === 'error'
            ? 'linear-gradient(90deg, #f44336, #ff7043)'
            : 'linear-gradient(90deg, #1565c0, #42a5f5)',
        }}
      />

      <DialogTitle sx={{ pt: 3, pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <WarningAmberRoundedIcon
          sx={{
            color: confirmColor === 'error' ? 'error.main' : 'primary.main',
            fontSize: 28,
            flexShrink: 0,
          }}
        />
        <Box component="span" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
          {title}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <DialogContentText sx={{ color: 'text.primary', fontSize: '0.95rem', lineHeight: 1.6 }}>
          {message}
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            color: 'text.secondary',
            borderColor: 'divider',
            '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' },
          }}
        >
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          color={confirmColor}
          variant="contained"
          disabled={loading}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            px: 3,
            boxShadow: confirmColor === 'error'
              ? '0 4px 14px rgba(244,67,54,0.35)'
              : '0 4px 14px rgba(21,101,192,0.35)',
            '&:hover': {
              boxShadow: confirmColor === 'error'
                ? '0 6px 20px rgba(244,67,54,0.5)'
                : '0 6px 20px rgba(21,101,192,0.5)',
            },
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
